import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { X, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_FORM = {
  brand_name: "Cela Seguro",
  description: "",
  col1_title: "Comunidade",
  col1_items: [{ label: "", url: "" }],
  col2_title: "Sobre",
  col2_text: "",
  copyright: "© {year} Cela Seguro · Feito com cuidado por e para a comunidade.",
  bg_color: "#1C2321",
  text_color: "#FDFBF7",
  accent_color: "#E87C55",
};

function ColorRow({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-xs font-bold text-brand-textMuted mb-1">{label}</label>
      <div className="flex gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-picker`} className="w-10 h-10 rounded-lg border border-brand-border cursor-pointer" />
        <Input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-brand-bg border-brand-border rounded-xl h-10 font-mono text-sm flex-1" />
      </div>
    </div>
  );
}

export default function EditFooterModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    api.get("/footer").then((r) => {
      if (r.data && Object.keys(r.data).length > 0) {
        setForm((f) => ({ ...DEFAULT_FORM, ...r.data, col1_items: r.data.col1_items?.length ? r.data.col1_items : [{ label: "", url: "" }] }));
      }
    }).catch(() => {});
  }, [open]);

  const updItem = (i, k, v) => setForm((f) => ({ ...f, col1_items: f.col1_items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, col1_items: [...f.col1_items, { label: "", url: "" }] }));
  const rmItem = (i) => setForm((f) => ({ ...f, col1_items: f.col1_items.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, col1_items: form.col1_items.filter((it) => it.label?.trim()) };
      await api.put("/moderation/footer", payload);
      toast.success("Rodapé atualizado");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-footer-modal" className="max-w-3xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Editar rodapé</DialogTitle>
          <DialogDescription className="text-brand-textMuted">Personalize nome da marca, textos, links, cores e copyright.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-6 mt-2">
          <fieldset className="space-y-3">
            <legend className="font-heading font-bold text-lg text-brand-text">Marca</legend>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Nome da marca</label>
              <Input data-testid="footer-brand" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Descrição (abaixo do logo)</label>
              <Textarea data-testid="footer-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl" />
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <div className="flex items-center justify-between">
              <legend className="font-heading font-bold text-lg text-brand-text">Coluna 1 (links)</legend>
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="footer-add-item" className="rounded-full font-bold"><Plus className="w-4 h-4 mr-1" /> Adicionar</Button>
            </div>
            <Input data-testid="footer-col1-title" value={form.col1_title} onChange={(e) => set("col1_title", e.target.value)} placeholder="Título da coluna" className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            <div className="space-y-2">
              {form.col1_items.map((it, i) => (
                <div key={`fi-${i}`} className="flex gap-2">
                  <Input data-testid={`footer-item-label-${i}`} value={it.label} onChange={(e) => updItem(i, "label", e.target.value)} placeholder="Texto" className="bg-brand-bg border-brand-border rounded-2xl h-11 flex-1" />
                  <Input data-testid={`footer-item-url-${i}`} value={it.url} onChange={(e) => updItem(i, "url", e.target.value)} placeholder="/rota ou https://..." className="bg-brand-bg border-brand-border rounded-2xl h-11 flex-1" />
                  {form.col1_items.length > 1 && (
                    <Button type="button" variant="outline" size="icon" onClick={() => rmItem(i)} data-testid={`footer-item-remove-${i}`} className="rounded-full border-red-200 text-red-700 hover:bg-red-50 shrink-0"><X className="w-4 h-4" /></Button>
                  )}
                </div>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-heading font-bold text-lg text-brand-text">Coluna 2 (texto)</legend>
            <Input data-testid="footer-col2-title" value={form.col2_title} onChange={(e) => set("col2_title", e.target.value)} placeholder="Título da coluna" className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            <Textarea data-testid="footer-col2-text" rows={3} value={form.col2_text} onChange={(e) => set("col2_text", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl" />
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-heading font-bold text-lg text-brand-text">Rodapé final</legend>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Linha de copyright</label>
              <Input data-testid="footer-copyright" value={form.copyright} onChange={(e) => set("copyright", e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11 font-mono text-sm" />
              <p className="text-xs text-brand-textMuted mt-1">Use <code>{"{year}"}</code> para inserir o ano automaticamente.</p>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="font-heading font-bold text-lg text-brand-text">Cores</legend>
            <div className="grid grid-cols-3 gap-3">
              <ColorRow label="Fundo" value={form.bg_color} onChange={(v) => set("bg_color", v)} testId="footer-bg-color" />
              <ColorRow label="Texto" value={form.text_color} onChange={(v) => set("text_color", v)} testId="footer-text-color" />
              <ColorRow label="Destaque (logo)" value={form.accent_color} onChange={(v) => set("accent_color", v)} testId="footer-accent-color" />
            </div>
          </fieldset>

          <DialogFooter className="gap-2 sticky bottom-0 bg-white pt-4 border-t border-brand-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full font-bold">Cancelar</Button>
            <Button type="submit" data-testid="footer-save-btn" disabled={saving} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
              {saving ? "Salvando..." : "Salvar rodapé"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
